import { SendEmailAuthLinkError } from "@redotech/customer-portal-sdk/rpc/schema/send-email-auth-link";
import { emailIsValid } from "@redotech/email";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { SocialLoginOption } from "@redotech/redo-model/auth/social-auth";
import { Step } from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { ClientError } from "@redotech/rpc/errors";
import { useContext, useState } from "react";
import { SettingsContext } from "../contexts/settings";
import {
  getHeaderText,
  getLoginGiftText,
  getWarrantyText,
  sendEmailAuthLinkErrorToText,
} from "./dynamic-text-utils";
export interface UseOneTimePasscodeResponse {
  texts: {
    emailLabel: string;
    emailHint: string;
    sendLink: string;
    title: string;
    subtitle: string;
    merchantName: string;
    emailConfirmationTitle: string;
    emailConfirmationSubtitle: string;
    didNotReceiveEmail: string;
    resendLink: string;
    giftButton: string;
    registerButton: string;
    dontHaveEmail: string;
  };
  logo: string;
  emailInput: string;
  onEmailChange: (email: string) => void;
  onSendLink: () => void;
  onResendLink: () => void;
  onBack: (() => void) | undefined;
  sendLinkDisabled: boolean;
  emailValidationError?: string;
  sendEmailError?: string;
  showEmailConfirmationPage: boolean;
  canResend: boolean;
  resendEmailError?: string;
  hideGiftOption: boolean;
  registrationEnabled: boolean;
  socialLoginOptions: SocialLoginOption[] | undefined;
}

export function useOneTimePasscode({
  onBack,
  nextUrl,
  title,
}: {
  onBack?: () => void;
  nextUrl?: string;
  title?: string;
}): UseOneTimePasscodeResponse {
  const settings = useContext(SettingsContext);
  const rpcClient = useRequiredContext(RpcClientContext);
  const [hasResentEmail, setHasResentEmail] = useState(false);
  const [emailValidation, setEmailValidation] = useState<string | undefined>(
    undefined,
  );
  const [emailInput, setEmailInput] = useState("");
  const onEmailChange = useHandler((email: string) => {
    if (!email) {
      setEmailValidation("Email is required");
    } else if (emailIsValid(email)) {
      setEmailValidation(undefined);
    } else {
      setEmailValidation("Invalid email");
    }
    setEmailInput(email);
  });

  const [sendLinkLoadState, sendLink] = useTriggerLoad(async () => {
    try {
      return await rpcClient.sendEmailAuthLink({
        email: emailInput,
        baseUrl: location.href,
        nextUrl,
      });
    } catch (e) {
      if (e instanceof ClientError) {
        const rpcError = e as ClientError<SendEmailAuthLinkError>;
        if (rpcError.code) {
          throw new Error(sendEmailAuthLinkErrorToText[rpcError.code]);
        }
      }

      throw new Error("Something went wrong sending the email.");
    }
  });

  const [resendTriggerLink, resendLink] = useTriggerLoad(async () => {
    setHasResentEmail(true);
    return await rpcClient.sendEmailAuthLink({
      email: emailInput,
      baseUrl: location.href,
      nextUrl,
    });
  });

  // If the merchant only offers returns for refund then we should hide the option for returning a gift,
  // since gift returns cannot be returned for refund.
  let hideGiftOption = true;
  if (
    settings?.packageProtection?.enabled &&
    settings.packageProtection.customerClaimsEnabled &&
    !settings.hideGiftOption
  ) {
    hideGiftOption = false;
  }
  if (settings?.returns?.enabled && hideGiftOption) {
    const returnOptionSteps = settings.returnFlow.steps.filter(
      (step) => step.type === Step.RETURN,
    );
    for (const returnOptionStep of returnOptionSteps) {
      for (const option of returnOptionStep.options) {
        if (option.method.type !== ReturnOptionMethod.REFUND) {
          hideGiftOption = false;
        }
      }
    }
  }

  return {
    texts: {
      emailLabel: "Email",
      emailHint: "Enter your email",
      sendLink: sendLinkLoadState.pending
        ? "Sending..."
        : settings?.returnPortalButtonText || "Send link",
      title: title ?? getHeaderText(settings),
      subtitle: "Enter your email to receive a unique login link.",
      merchantName: settings?.merchantName || "Unknown Merchant",
      emailConfirmationTitle: "Check your email",
      emailConfirmationSubtitle: `We sent an email to ${emailInput}. Open the email and click the link to login to our customer portal.`,
      didNotReceiveEmail: "Didn't receive an email?",
      resendLink: hasResentEmail
        ? "We sent another link to your email"
        : "Resend email",
      giftButton: getLoginGiftText(settings),
      registerButton: getWarrantyText(settings),
      dontHaveEmail: "Log in with an order number",
    },
    onEmailChange,
    onSendLink: sendLink,
    onResendLink: resendLink,
    emailInput,
    sendLinkDisabled:
      sendLinkLoadState.pending ||
      emailValidation !== undefined ||
      emailInput === "",
    emailValidationError: emailValidation,
    resendEmailError: resendTriggerLink.error?.message,
    sendEmailError: sendLinkLoadState.error?.message,
    logo: settings?.theme.logo_image || "",
    showEmailConfirmationPage:
      sendLinkLoadState.value !== undefined &&
      sendLinkLoadState.error === undefined,
    canResend: !hasResentEmail,
    hideGiftOption,
    registrationEnabled: settings?.warranties?.registrationEnabled || false,
    socialLoginOptions: settings?.theme?.socialLoginOptions,
    onBack,
  };
}
